/**
 * RepConnect - Availability Service
 *
 * Manages physician availability and prevents double-booking
 * Part of Phase 2: Multi-Physician Office Lunch Support
 */

import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';
import { customerLocationService } from './locationService';

/**
 * Helper: Convert 12-hour time format to 24-hour format
 * @param {string} time12h - Time in "HH:MM AM/PM" format
 * @returns {string} Time in "HH:MM" 24-hour format
 */
const convertTo24Hour = (time12h) => {
  if (!time12h) return '';

  const [time, modifier] = time12h.split(' ');
  let [hours, minutes] = time.split(':');

  if (hours === '12') {
    hours = modifier === 'AM' ? '00' : '12';
  } else if (modifier === 'PM') {
    hours = String(parseInt(hours, 10) + 12);
  }

  return `${hours.padStart(2, '0')}:${minutes}`;
};

/**
 * Helper: Convert 24-hour time format to 12-hour format
 * @param {string} time24h - Time in "HH:MM" 24-hour format
 * @returns {string} Time in "HH:MM AM/PM" format
 */
const convertTo12Hour = (time24h) => {
  if (!time24h) return '';

  let [hours, minutes] = time24h.split(':');
  hours = parseInt(hours, 10);

  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${modifier}`;
};

/**
 * Helper: Check if a date is fully blocked by any blocked period (all-day block)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {Array} blockedPeriods - Array of blocked period objects
 * @returns {boolean} True if the entire day is blocked
 */
const isDateFullyBlocked = (dateString, blockedPeriods) => {
  if (!blockedPeriods || blockedPeriods.length === 0) return false;

  const checkDate = new Date(dateString + 'T12:00:00');

  return blockedPeriods.some(period => {
    const startDate = new Date(period.startDate + 'T00:00:00');
    const endDate = new Date(period.endDate + 'T23:59:59');
    const isInRange = checkDate >= startDate && checkDate <= endDate;
    // Only consider it fully blocked if it's explicitly an all-day block (allDay === true)
    return isInRange && period.allDay === true;
  });
};

/**
 * Helper: Check if a time slot is blocked by any blocked period
 * @param {string} slotStart - Slot start time in 24-hour format (HH:MM)
 * @param {string} slotEnd - Slot end time in 24-hour format (HH:MM)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {Array} blockedPeriods - Array of blocked period objects
 * @returns {boolean} True if the slot is blocked
 */
const isTimeSlotBlocked = (slotStart, slotEnd, dateString, blockedPeriods) => {
  if (!blockedPeriods || blockedPeriods.length === 0) return false;

  const checkDate = new Date(dateString + 'T12:00:00');

  for (const period of blockedPeriods) {
    const periodStart = new Date(period.startDate + 'T00:00:00');
    const periodEnd = new Date(period.endDate + 'T23:59:59');

    // Check if date is in range
    if (checkDate < periodStart || checkDate > periodEnd) continue;

    // If all-day block, the slot is blocked
    // Check explicitly for allDay === true (not undefined/null)
    if (period.allDay === true) {
      logger.debug('Time slot blocked by all-day block', { slotStart, dateString, period: period.reason });
      return true;
    }

    // For time-specific blocks, check if slot overlaps with blocked time
    if (period.startTime && period.endTime) {
      // Check for overlap: slot overlaps if it starts before block ends AND ends after block starts
      const blocksSlot = slotStart < period.endTime && slotEnd > period.startTime;
      if (blocksSlot) {
        logger.debug('Time slot blocked by time-specific block', {
          slotStart,
          slotEnd,
          blockStart: period.startTime,
          blockEnd: period.endTime,
          reason: period.reason
        });
        return true;
      }
    }
  }

  return false;
};

/**
 * Helper: Filter time slots by physician's declared availability and blocked periods
 * @param {Object} weeklyAvailability - Weekly availability object from provider_availability
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} bookedTimes24 - Array of booked times in 24-hour format
 * @param {Array} blockedPeriods - Optional array of blocked period objects
 * @returns {Array} Array of available time slots in 12-hour format
 */
const filterTimeSlotsByAvailability = (weeklyAvailability, date, bookedTimes24, blockedPeriods = []) => {
  if (!weeklyAvailability) return [];

  // First check if entire day is blocked
  if (isDateFullyBlocked(date, blockedPeriods)) {
    logger.debug('filterTimeSlotsByAvailability: Entire day is blocked', { date });
    return [];
  }

  const dateObj = new Date(date + 'T12:00:00');
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayKey = dayNames[dateObj.getDay()];

  const daySlots = weeklyAvailability[dayKey] || [];

  logger.debug('filterTimeSlotsByAvailability', {
    date,
    dayKey,
    dayslotsCount: daySlots.length,
    bookedTimes24,
    blockedPeriodsCount: blockedPeriods.length
  });

  if (daySlots.length === 0) return [];

  // Filter by availability, booked times, and blocked periods
  const availableSlots = [];

  for (const slot of daySlots) {
    const startTime24 = slot.startTime; // Already in 24-hour format from DB
    const endTime24 = slot.endTime || startTime24; // Use end time if available
    const isBooked = bookedTimes24.includes(startTime24);
    const isBlocked = isTimeSlotBlocked(startTime24, endTime24, date, blockedPeriods);

    logger.debug(`Checking slot availability`, {
      time: startTime24,
      booked: isBooked,
      blocked: isBlocked
    });

    // Check if this time is not already booked and not blocked
    if (!isBooked && !isBlocked) {
      availableSlots.push({ time12: convertTo12Hour(startTime24), time24: startTime24 });
    }
  }

  logger.debug('Available slots after filtering', { slots: availableSlots.map(s => s.time12) });

  // Sort by 24-hour time for chronological order, then return 12-hour format
  return availableSlots
    .sort((a, b) => a.time24.localeCompare(b.time24))
    .map(slot => slot.time12);
};

/**
 * Availability Service
 * Handles scheduling logic, calendar availability, and conflict detection
 */
export const availabilityService = {
  /**
   * Check if a physician is available at a specific location on a given day
   * @param {string} customerId - UUID of the physician
   * @param {string} locationId - UUID of the practice location
   * @param {Date|string} date - Date to check (Date object or ISO string)
   * @returns {Promise<boolean>} True if available, false if not
   */
  isPhysicianAvailableOnDate: async (customerId, locationId, date) => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dateObj.getDay()];
      const dayColumn = `works_${dayOfWeek}`;

      // Check if physician works at this location on this day of week
      let { data, error } = await supabase
        .from('customer_locations')
        .select(dayColumn)
        .eq('customer_id', customerId)
        .eq('location_id', locationId)
        .maybeSingle();

      // If not found, check if there's office staff delegating for this physician
      if (!data) {
        const delegatingUser = await customerLocationService.getDelegatingUserForPhysician(customerId);

        if (delegatingUser) {
          const { data: staffData, error: staffError } = await supabase
            .from('customer_locations')
            .select(dayColumn)
            .eq('customer_id', delegatingUser.id)
            .eq('location_id', locationId)
            .maybeSingle();

          if (!staffError && staffData) {
            data = staffData;
            error = null;
          }
        }
      }

      if (error) {
        throw error;
      }

      if (!data) {
        // Customer not linked to this location
        return false;
      }

      return data[dayColumn] === true;
    } catch (error) {
      logger.error('Error checking physician availability on date:', error);
      throw error;
    }
  },

  /**
   * Check if a physician has a conflicting meeting at the same date/time
   * @param {string} customerId - UUID of the physician
   * @param {string} date - Meeting date (YYYY-MM-DD)
   * @param {string} time - Meeting time (HH:MM)
   * @param {string} excludeMeetingId - Optional meeting ID to exclude (for updates)
   * @returns {Promise<boolean>} True if there's a conflict, false if available
   */
  hasTimeConflict: async (customerId, date, time, excludeMeetingId = null) => {
    try {
      let query = supabase
        .from('meeting_attendees')
        .select(`
          id,
          meetings!inner (
            id,
            meeting_date,
            start_time,
            status
          )
        `)
        .eq('user_id', customerId)
        .eq('meetings.meeting_date', date)
        .eq('meetings.start_time', time)
        .in('meetings.status', ['pending', 'confirmed', 'approved']);

      if (excludeMeetingId) {
        query = query.neq('meetings.id', excludeMeetingId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data && data.length > 0;
    } catch (error) {
      logger.error('Error checking time conflict:', error);
      throw error;
    }
  },

  /**
   * Get all meetings for a physician on a specific date
   * @param {string} customerId - UUID of the physician
   * @param {string} date - Date to check (YYYY-MM-DD)
   * @returns {Promise<Array>} Array of meetings on that date
   */
  getMeetingsOnDate: async (customerId, date) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .select(`
          *,
          meetings!inner (
            id,
            meeting_date,
            start_time,
            end_time,
            status,
            location_name,
            location_address,
            restaurant_name,
            sales_rep_id
          )
        `)
        .eq('user_id', customerId)
        .eq('meetings.meeting_date', date)
        .in('meetings.status', ['pending', 'confirmed', 'approved', 'completed'])
        .order('meetings(start_time)', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting meetings on date:', error);
      throw error;
    }
  },

  /**
   * Get all booked times at a location on a specific date (for office lunch scheduling)
   * This checks ALL meetings at the location, not just one physician's schedule
   * @param {string} locationId - UUID of the practice location
   * @param {string} date - Date to check (YYYY-MM-DD)
   * @returns {Promise<Array>} Array of booked start times in 24-hour format
   */
  getLocationBookedTimes: async (locationId, date) => {
    try {
      logger.debug('getLocationBookedTimes called', { locationId, date });

      const { data, error } = await supabase
        .from('meetings')
        .select('start_time')
        .eq('location_id', locationId)
        .eq('meeting_date', date)
        .in('status', ['pending', 'confirmed', 'approved', 'completed']);

      if (error) throw error;

      // Return array of start times in HH:MM format (strip seconds if present)
      const bookedTimes = (data || []).map(meeting => {
        const time = meeting.start_time;
        // Normalize to HH:MM format (remove seconds if present)
        return time.substring(0, 5); // "08:00:00" -> "08:00"
      });
      logger.debug('Location booked times normalized', { count: bookedTimes.length, times: bookedTimes });

      return bookedTimes;
    } catch (error) {
      logger.error('Error getting location booked times', { error: error.message });
      throw error;
    }
  },

  /**
   * Get provider's available time slots for a specific day of week at a location
   * @param {string} customerId - UUID of the physician
   * @param {string} locationId - UUID of the practice location
   * @param {string} dayOfWeek - Day name: 'monday', 'tuesday', etc.
   * @returns {Promise<Array>} Array of time slots in HH:MM format (e.g., ["08:00", "12:00", "15:00"])
   */
  getProviderTimeSlotsForDay: async (customerId, locationId, dayOfWeek) => {
    try {
      logger.debug('getProviderTimeSlotsForDay called', { customerId, locationId, dayOfWeek });

      // Query provider_availability table for this location
      let { data: availabilityData, error: availError } = await supabase
        .from('provider_availability')
        .select('weekly_availability')
        .eq('provider_id', customerId)
        .eq('location_id', locationId)
        .maybeSingle();

      if (availError) {
        logger.error('Error loading provider availability', { customerId, locationId, error: availError.message });
        return []; // Return empty - no artificial defaults
      }

      // If no location-specific availability, check if there's a delegating user (office staff)
      if (!availabilityData) {
        logger.debug('getProviderTimeSlotsForDay: No location-specific availability found, checking for delegating user');

        const delegatingUser = await customerLocationService.getDelegatingUserForPhysician(customerId);
        if (delegatingUser) {
          logger.debug('getProviderTimeSlotsForDay: Found delegating user, checking their availability', {
            physicianId: customerId,
            delegatingUserId: delegatingUser.id
          });

          const { data: staffAvail, error: staffError } = await supabase
            .from('provider_availability')
            .select('weekly_availability')
            .eq('provider_id', customerId) // Still query with physician ID since saveLocationAvailability redirects
            .eq('location_id', locationId)
            .maybeSingle();

          if (!staffError && staffAvail) {
            availabilityData = staffAvail;
          }
        }
      }

      // If still no location-specific availability, try general availability (no location_id)
      if (!availabilityData) {
        logger.debug('getProviderTimeSlotsForDay: No location-specific availability found, checking general availability');
        const { data: generalAvail, error: genError } = await supabase
          .from('provider_availability')
          .select('weekly_availability')
          .eq('provider_id', customerId)
          .is('location_id', null)
          .maybeSingle();

        if (genError) {
          logger.error('Error loading general availability', { customerId, error: genError.message });
          return []; // Return empty - no artificial defaults
        }

        if (!generalAvail?.weekly_availability) {
          logger.warn('getProviderTimeSlotsForDay: No provider availability set for customer', { customerId, locationId, dayOfWeek });
          return []; // Return empty - provider needs to set up availability
        }

        // Extract time slots from general weekly_availability JSONB
        const daySlots = generalAvail.weekly_availability[dayOfWeek] || [];
        const timeSlots = daySlots.map(slot => slot.startTime).filter(Boolean);

        logger.debug('getProviderTimeSlotsForDay: Using general availability time slots', { count: timeSlots.length, timeSlots });
        return timeSlots; // Return actual slots (may be empty)
      }

      // Extract time slots from location-specific weekly_availability JSONB
      const weeklyAvailability = availabilityData.weekly_availability;
      if (!weeklyAvailability || !weeklyAvailability[dayOfWeek]) {
        logger.debug('getProviderTimeSlotsForDay: No availability for day of week', { customerId, locationId, dayOfWeek });
        return []; // Return empty - no slots configured for this day
      }

      const daySlots = weeklyAvailability[dayOfWeek];
      const timeSlots = daySlots.map(slot => slot.startTime).filter(Boolean);

      logger.debug('getProviderTimeSlotsForDay: Found time slots from provider_availability', { count: timeSlots.length, timeSlots });

      return timeSlots; // Return actual slots (may be empty)
    } catch (error) {
      logger.error('Error in getProviderTimeSlotsForDay', { error: error.message });
      return []; // Return empty on error - no artificial defaults
    }
  },

  /**
   * Get availability status for a date range
   * Used for calendar color coding (green = available, yellow = partially booked, red = fully booked)
   * @param {string} customerId - UUID of the physician
   * @param {string} locationId - UUID of the practice location
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} Object with dates as keys and availability status as values
   */
  getDateAvailabilityRange: async (customerId, locationId, startDate, endDate) => {
    try {
      // Get location availability (day of week)
      // First try with the physician's ID directly
      let { data: locationData, error: locationError } = await supabase
        .from('customer_locations')
        .select('*')
        .eq('customer_id', customerId)
        .eq('location_id', locationId)
        .maybeSingle();

      // If not found, check if there's office staff delegating for this physician
      // (handles placeholder physicians created during office staff onboarding)
      if (!locationData) {
        logger.debug('No customer_locations for physician, checking for delegating user', { customerId, locationId });

        const delegatingUser = await customerLocationService.getDelegatingUserForPhysician(customerId);

        if (delegatingUser) {
          logger.debug('Found delegating user, fetching their location data', {
            physicianId: customerId,
            delegatingUserId: delegatingUser.id
          });

          const { data: staffLocationData, error: staffError } = await supabase
            .from('customer_locations')
            .select('*')
            .eq('customer_id', delegatingUser.id)
            .eq('location_id', locationId)
            .maybeSingle();

          if (!staffError && staffLocationData) {
            logger.info('Using location data from delegating office staff', {
              physicianId: customerId,
              staffId: delegatingUser.id
            });
            locationData = staffLocationData;
            locationError = null;
          }
        }
      }

      if (locationError) {
        throw locationError;
      }

      if (!locationData) {
        // Not linked to this location
        logger.debug('No location data found for physician or delegating user', { customerId, locationId });
        return {};
      }

      // Get all meetings at this location in date range (location-wide check for office lunches)
      const { data: meetingsData, error: meetingsError } = await supabase
        .from('meetings')
        .select('id, meeting_date, start_time, status')
        .eq('location_id', locationId)
        .gte('meeting_date', startDate)
        .lte('meeting_date', endDate)
        .in('status', ['pending', 'confirmed', 'approved']);

      if (meetingsError) throw meetingsError;

      // Get blocked periods and weekly availability from provider_availability
      let { data: providerAvailData, error: provAvailError } = await supabase
        .from('provider_availability')
        .select('blocked_periods, weekly_availability')
        .eq('provider_id', customerId)
        .eq('location_id', locationId)
        .maybeSingle();

      logger.info('getDateAvailabilityRange: Query for provider_availability', {
        customerId,
        locationId,
        foundData: !!providerAvailData,
        hasBlockedPeriods: providerAvailData?.blocked_periods?.length > 0,
        hasWeeklyAvailability: !!providerAvailData?.weekly_availability,
        error: provAvailError?.message
      });

      let blockedPeriods = providerAvailData?.blocked_periods || [];
      let weeklyAvailability = providerAvailData?.weekly_availability || null;

      // If no location-specific data, check general availability (null location_id)
      if (!providerAvailData) {
        logger.debug('getDateAvailabilityRange: No location-specific data, checking general availability');
        const { data: generalProvAvail } = await supabase
          .from('provider_availability')
          .select('blocked_periods, weekly_availability')
          .eq('provider_id', customerId)
          .is('location_id', null)
          .maybeSingle();

        logger.debug('getDateAvailabilityRange: General availability result', {
          foundData: !!generalProvAvail,
          hasBlockedPeriods: generalProvAvail?.blocked_periods?.length > 0
        });

        blockedPeriods = generalProvAvail?.blocked_periods || [];
        weeklyAvailability = generalProvAvail?.weekly_availability || null;
      }

      // Log final data being used
      logger.info('getDateAvailabilityRange: Using availability data', {
        blockedPeriodsCount: blockedPeriods.length,
        weeklyAvailabilityDays: weeklyAvailability ? Object.keys(weeklyAvailability).filter(k => weeklyAvailability[k]?.length > 0) : []
      });

      if (blockedPeriods.length > 0) {
        logger.info('getDateAvailabilityRange: Found blocked periods', {
          customerId,
          locationId,
          count: blockedPeriods.length,
          periods: blockedPeriods.map(p => ({
            startDate: p.startDate,
            endDate: p.endDate,
            allDay: p.allDay,
            startTime: p.startTime,
            endTime: p.endTime
          }))
        });
      }

      // Build availability map
      const availability = {};
      // IMPORTANT: Parse dates in local time to avoid timezone issues
      const currentDate = new Date(startDate + 'T12:00:00');
      const endDateObj = new Date(endDate + 'T12:00:00');

      while (currentDate <= endDateObj) {
        // Use local date components, not UTC
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        const dayOfWeekIndex = currentDate.getDay();
        const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeekIndex];
        const dayColumn = `works_${dayOfWeek}`;

        // Check if physician works this day
        const worksThisDay = locationData[dayColumn] === true;

        // Check if this date is fully blocked (all-day block)
        const isFullyBlocked = isDateFullyBlocked(dateStr, blockedPeriods);

        if (isFullyBlocked) {
          availability[dateStr] = 'blocked'; // Gray/blocked - entire day blocked
          logger.debug(`Date blocked by all-day block - ${dateStr}`);
        } else if (!worksThisDay) {
          availability[dateStr] = 'unavailable'; // Not working this day
        } else {
          // Get provider's actual available time slots for this day of week
          // Use the already-fetched weeklyAvailability - NO fallback to artificial defaults
          let totalAvailableSlots = [];
          if (weeklyAvailability && weeklyAvailability[dayOfWeek]) {
            const daySlots = weeklyAvailability[dayOfWeek];
            totalAvailableSlots = daySlots.map(slot => slot.startTime).filter(Boolean);
          }

          // If no slots configured for this day, mark as unavailable (no artificial defaults)
          if (totalAvailableSlots.length === 0) {
            availability[dateStr] = 'unavailable';
            currentDate.setDate(currentDate.getDate() + 1);
            continue;
          }

          // Use info level for specific dates to debug calendar issues
          const logLevel = dateStr === '2025-12-18' ? 'info' : 'debug';
          logger[logLevel](`Date availability check - ${dateStr}`, {
            dayOfWeek,
            totalAvailableSlots,
            weeklyAvailabilityExists: !!weeklyAvailability,
            slotsCount: totalAvailableSlots.length
          });

          // Get unique booked times on this date (location-wide meetings)
          const bookedTimesOnDate = meetingsData
            ?.filter(m => m.meeting_date === dateStr)
            .map(m => m.start_time?.substring(0, 5)) // Normalize to HH:MM
            .filter((time, index, self) => time && self.indexOf(time) === index) // Unique times only
            || [];

          // Check how many slots are blocked by time-specific blocks
          // Use the actual slot endTime from weeklyAvailability if available
          let blockedSlotsCount = 0;
          const daySlotsWithEndTime = weeklyAvailability?.[dayOfWeek] || [];

          for (const slotTime of totalAvailableSlots) {
            // Find the matching slot to get its endTime
            const matchingSlot = daySlotsWithEndTime.find(s => s.startTime === slotTime);
            let endTime = matchingSlot?.endTime;

            // If no endTime, assume 1-hour slots
            if (!endTime) {
              endTime = slotTime.replace(/^(\d{2}):(\d{2})/, (match, h, m) => {
                const hour = (parseInt(h) + 1) % 24;
                return `${hour.toString().padStart(2, '0')}:${m}`;
              });
            }

            if (isTimeSlotBlocked(slotTime, endTime, dateStr, blockedPeriods)) {
              blockedSlotsCount++;
              logger.debug(`Slot ${slotTime}-${endTime} is blocked on ${dateStr}`);
            }
          }

          const uniqueBookedSlots = bookedTimesOnDate.length;
          const totalSlots = totalAvailableSlots.length;
          const unavailableSlots = uniqueBookedSlots + blockedSlotsCount;

          // Use info level for Dec 18 to debug calendar issue
          logger[logLevel](`Availability analysis - ${dateStr}`, {
            bookedSlotsCount: uniqueBookedSlots,
            blockedSlotsCount,
            totalSlots: totalSlots,
            unavailableSlots,
            bookedTimes: bookedTimesOnDate,
            availableTimes: totalAvailableSlots,
            willShowAs: unavailableSlots >= totalSlots ? 'fully_booked' : (unavailableSlots > 0 ? 'partially_booked' : 'available')
          });

          if (unavailableSlots >= totalSlots) {
            availability[dateStr] = 'fully_booked'; // Red - all slots unavailable
          } else if (unavailableSlots > 0) {
            availability[dateStr] = 'partially_booked'; // Yellow - some slots still available
          } else {
            availability[dateStr] = 'available'; // Green - no bookings or blocks
          }

          logger.debug(`Date status determined - ${dateStr}`, { status: availability[dateStr] });
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return availability;
    } catch (error) {
      logger.error('Error getting date availability range:', error);
      throw error;
    }
  },

  /**
   * Find available time slots for a physician on a specific date at a specific location
   * @param {string} customerId - UUID of the physician
   * @param {string} date - Date to check (YYYY-MM-DD)
   * @param {string} locationId - UUID of the practice location (optional)
   * @param {Array} preferredTimes - Optional array of preferred times to check first
   * @param {boolean} checkLocationWide - If true, checks all meetings at location (for office lunches)
   * @returns {Promise<Array>} Array of available time slots in "HH:MM AM/PM" format
   */
  getAvailableTimeSlots: async (customerId, date, locationId = null, preferredTimes = ['11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM'], checkLocationWide = false) => {
    try {
      logger.debug('getAvailableTimeSlots called', {
        customerId,
        date,
        locationId,
        checkLocationWide,
        preferredTimes
      });

      // Get existing meetings on this date
      // For office lunches, check ALL meetings at the location (not just one physician)
      let bookedTimes24;
      if (checkLocationWide && locationId) {
        bookedTimes24 = await availabilityService.getLocationBookedTimes(locationId, date);
        logger.debug('Using location-wide booked times', { bookedTimes24 });
      } else {
        const meetings = await availabilityService.getMeetingsOnDate(customerId, date);
        bookedTimes24 = meetings.map(m => m.meetings.start_time); // 24-hour format from DB
        logger.debug('Using physician-specific booked times', { bookedTimes24 });
      }

      // Get physician's declared availability for this location (including blocked_periods)
      const { data: availabilityData, error: availError } = await supabase
        .from('provider_availability')
        .select('weekly_availability, blocked_periods')
        .eq('provider_id', customerId)
        .eq('location_id', locationId)
        .maybeSingle();

      if (availError) {
        logger.error('Error loading provider availability:', availError);
        return []; // Return empty - no artificial defaults
      }

      // Log if we found blocked periods
      if (availabilityData?.blocked_periods?.length > 0) {
        logger.info('Found blocked periods for provider', {
          customerId,
          locationId,
          blockedPeriodsCount: availabilityData.blocked_periods.length,
          blockedPeriods: availabilityData.blocked_periods.map(p => ({
            startDate: p.startDate,
            endDate: p.endDate,
            allDay: p.allDay,
            startTime: p.startTime,
            endTime: p.endTime,
            reason: p.reason
          }))
        });
      }

      // If no location-specific availability, check general availability
      if (!availabilityData) {
        const { data: generalAvail, error: genError } = await supabase
          .from('provider_availability')
          .select('weekly_availability, blocked_periods')
          .eq('provider_id', customerId)
          .is('location_id', null)
          .maybeSingle();

        if (genError) {
          logger.error('Error loading general availability:', genError);
          return []; // Return empty - no artificial defaults
        }

        if (!generalAvail?.weekly_availability) {
          // No availability set - return empty (provider needs to configure availability)
          logger.warn('getAvailableTimeSlots: No availability configured for provider', { customerId, locationId });
          return [];
        }

        // Use general availability with blocked periods
        return filterTimeSlotsByAvailability(
          generalAvail.weekly_availability,
          date,
          bookedTimes24,
          generalAvail.blocked_periods || []
        );
      }

      // Use location-specific availability with blocked periods
      return filterTimeSlotsByAvailability(
        availabilityData.weekly_availability,
        date,
        bookedTimes24,
        availabilityData.blocked_periods || []
      );
    } catch (error) {
      logger.error('Error getting available time slots:', error);
      throw error;
    }
  },

  /**
   * Validate meeting can be scheduled (check location availability + time conflicts)
   * @param {Object} meetingData - Meeting details to validate
   * @param {string} meetingData.customer_id - UUID of the physician
   * @param {string} meetingData.location_id - UUID of the practice location
   * @param {string} meetingData.meeting_date - Date (YYYY-MM-DD)
   * @param {string} meetingData.meeting_time - Time (HH:MM)
   * @param {string} meetingData.exclude_meeting_id - Optional meeting ID to exclude
   * @returns {Promise<Object>} { isValid: boolean, reason: string }
   */
  validateMeetingSchedule: async (meetingData) => {
    try {
      const {
        customer_id,
        location_id,
        meeting_date,
        meeting_time,
        exclude_meeting_id
      } = meetingData;

      // Check 1: Physician works at this location on this day
      const worksThisDay = await availabilityService.isPhysicianAvailableOnDate(
        customer_id,
        location_id,
        meeting_date
      );

      if (!worksThisDay) {
        return {
          isValid: false,
          reason: 'Physician does not work at this location on this day of the week'
        };
      }

      // Check 2: No time conflict
      const hasConflict = await availabilityService.hasTimeConflict(
        customer_id,
        meeting_date,
        meeting_time,
        exclude_meeting_id
      );

      if (hasConflict) {
        return {
          isValid: false,
          reason: 'Physician already has a meeting at this date and time'
        };
      }

      return {
        isValid: true,
        reason: 'Meeting can be scheduled'
      };
    } catch (error) {
      logger.error('Error validating meeting schedule:', error);
      throw error;
    }
  },

  /**
   * Check if multiple physicians are all available at a location on a specific date/time
   * Used for multi-physician office lunches
   * @param {Array} customerIds - Array of physician UUIDs
   * @param {string} locationId - UUID of the practice location
   * @param {string} date - Date (YYYY-MM-DD)
   * @param {string} time - Time (HH:MM)
   * @returns {Promise<Object>} { allAvailable: boolean, unavailablePhysicians: Array }
   */
  checkMultiPhysicianAvailability: async (customerIds, locationId, date, time) => {
    try {
      const unavailablePhysicians = [];

      for (const customerId of customerIds) {
        const validation = await availabilityService.validateMeetingSchedule({
          customer_id: customerId,
          location_id: locationId,
          meeting_date: date,
          meeting_time: time
        });

        if (!validation.isValid) {
          unavailablePhysicians.push({
            customerId,
            reason: validation.reason
          });
        }
      }

      return {
        allAvailable: unavailablePhysicians.length === 0,
        unavailablePhysicians
      };
    } catch (error) {
      logger.error('Error checking multi-physician availability:', error);
      throw error;
    }
  },

  /**
   * Get physician's typical meal time preference
   * @param {string} customerId - UUID of the physician
   * @returns {Promise<string|null>} Preferred meal time or null
   */
  getPreferredMealTime: async (customerId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('preferred_meal_time')
        .eq('id', customerId)
        .maybeSingle();

      if (error) throw error;
      return data?.preferred_meal_time || null;
    } catch (error) {
      logger.error('Error getting preferred meal time:', error);
      throw error;
    }
  },

  /**
   * Get next available date for a physician at a location
   * @param {string} customerId - UUID of the physician
   * @param {string} locationId - UUID of the practice location
   * @param {string} startDate - Start searching from this date (YYYY-MM-DD)
   * @param {number} maxDaysToCheck - Maximum days to search ahead (default 30)
   * @returns {Promise<string|null>} Next available date or null
   */
  getNextAvailableDate: async (customerId, locationId, startDate, maxDaysToCheck = 30) => {
    try {
      const start = new Date(startDate);
      const endDate = new Date(start);
      endDate.setDate(endDate.getDate() + maxDaysToCheck);

      const availability = await availabilityService.getDateAvailabilityRange(
        customerId,
        locationId,
        startDate,
        endDate.toISOString().split('T')[0]
      );

      // Find first available or partially_booked date
      for (const [date, status] of Object.entries(availability)) {
        if (status === 'available' || status === 'partially_booked') {
          return date;
        }
      }

      return null; // No availability found in the range
    } catch (error) {
      logger.error('Error getting next available date:', error);
      throw error;
    }
  }
};
